import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { prepareAlgoliaPayload } from '../../util/algolia';
import moment from 'moment';
import omit from 'lodash/omit';

import { types as sdkTypes, createImageVariantConfig } from '../../util/sdkLoader';
import { denormalisedResponseEntities, ensureOwnListing, mailTime, sendEventMailData, templateIds } from '../../util/data';
import {
  getDefaultTimeZoneOnBrowser,
  getStartOf,
  getStartOfWeek,
  monthIdString,
  parseDateFromISO8601,
  stringifyDateToISO8601,
} from '../../util/dates';
import { uniqueBy } from '../../util/generators';
import { storableError } from '../../util/errors';
import * as log from '../../util/log';
import { parse } from '../../util/urlHelpers';
import { isUserAuthorized } from '../../util/userHelpers';
import { isBookingProcessAlias } from '../../transactions/transaction';

import { addMarketplaceEntities, getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import {
  createStripeAccount,
  updateStripeAccount,
  fetchStripeAccount,
} from '../../ducks/stripeConnectAccount.duck';
import { currentUserShowSuccess, fetchCurrentUser } from '../../ducks/user.duck';
import {
  createAlgoliaData,
  crudBusinesses,
  crudEvents,
  fetchNewCategories,
  onCancelScheduledMail,
  onSendEventMatchMail,
  onSetScheduledMail,
  RFPUpdateDetails,
  updateAlgoliaData
} from '../../util/api';
import {
  BUSINESS_LISTING_TYPE_DRAFT,
  BUSINESS_LISTING_TYPE_PUBLISHED,
  BUSINESS_SCHEDULLED_MAIL,
  EVENT_SCHEDULLED_MAIL,
  LISTING_STATE_PUBLISHED,
  LISTING_TYPE_BUSINESS,
  LISTING_TYPE_EVENT,
} from '../../util/types';
import { getCategories } from '../SignUpStepsPage/SignUpStepsPage.duck';
import axios from 'axios';

const REACT_APP_ALGOLIA_COMPANY_LISTING_INDEX = process.env.REACT_APP_ALGOLIA_COMPANY_LISTING_INDEX;
const REACT_APP_ALGOLIA_EVENT_LISTING_INDEX = process.env.REACT_APP_ALGOLIA_EVENT_LISTING_INDEX;

const REACT_APP_MARKETPLACE_ROOT_URL = process.env.REACT_APP_MARKETPLACE_ROOT_URL;
const SendTimeMail = 10;

const { UUID, LatLng } = sdkTypes;

// Create array of N items where indexing starts from 1
const getArrayOfNItems = n =>
  Array(n)
    .fill()
    .map((v, i) => i + 1)
    .slice(1);

// Return an array of image ids
const imageIds = images => {
  // For newly uploaded image the UUID can be found from "img.imageId"
  // and for existing listing images the id is "img.id"
  return images ? images.map(img => img.imageId || img.id) : null;
};

// After listing creation & update, we want to make sure that uploadedImages state is cleaned
const updateUploadedImagesState = (state, payload) => {
  const { uploadedImages, uploadedImagesOrder } = state;

  // Images attached to listing entity
  const attachedImages = payload?.data?.relationships?.images?.data || [];
  const attachedImageUUIDStrings = attachedImages.map(img => img.id.uuid);

  // Uploaded images (which are propably not yet attached to listing)
  const unattachedImages = Object.values(state.uploadedImages);
  const duplicateImageEntities = unattachedImages.filter(unattachedImg =>
    attachedImageUUIDStrings.includes(unattachedImg.imageId?.uuid)
  );
  return duplicateImageEntities.length > 0
    ? {
      uploadedImages: {},
      uploadedImagesOrder: [],
    }
    :
    {
      uploadedImages,
      uploadedImagesOrder,
    };
};

const getImageVariantInfo = listingImageConfig => {
  const { aspectWidth = 1, aspectHeight = 1, variantPrefix = 'listing-card' } = listingImageConfig;
  const aspectRatio = aspectHeight / aspectWidth;
  const fieldsImage = [`variants.${variantPrefix}`, `variants.${variantPrefix}-2x`];

  return {
    fieldsImage,
    imageVariants: {
      ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
      ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
    },
  };
};

const sortExceptionsByStartTime = (a, b) => {
  return a.attributes.start.getTime() - b.attributes.start.getTime();
};

// ================ Async Thunks ================ //

//////////////////
// Show Listing //
//////////////////
export const showListingThunk = createAsyncThunk(
  'EditListingPage/showListing',
  ({ actionPayload, config }, { dispatch, rejectWithValue, extra: sdk }) => {
    const imageVariantInfo = getImageVariantInfo(config.layout.listingImage);
    const queryParams = {
      include: ['author', 'images', 'currentStock'],
      'fields.image': imageVariantInfo.fieldsImage,
      ...imageVariantInfo.imageVariants,
    };

    return sdk.ownListings
      .show({ ...actionPayload, ...queryParams })
      .then(response => {
        // EditListingPage fetches new listing data, which also needs to be added to global data
        dispatch(addMarketplaceEntities(response));
        return response;
      })
      .catch(e => {
        return rejectWithValue(storableError(e));
      });
  }
);
// Backward compatible wrappers for the thunks
export const requestShowListing = (actionPayload, config) => (dispatch, getState, sdk) => {
  return dispatch(showListingThunk({ actionPayload, config })).unwrap();
};

///////////////
// Set Stock //
///////////////
export const setStockThunk = createAsyncThunk(
  'EditListingPage/setStock',
  ({ listingId, oldTotal, newTotal }, { dispatch, rejectWithValue, extra: sdk }) => {
    return sdk.stock
      .compareAndSet({ listingId, oldTotal, newTotal }, { expand: true })
      .then(response => {
        // NOTE: compareAndSet returns the stock resource of the listing.
        // We update client app's internal state with these updated API entities.
        dispatch(addMarketplaceEntities(response));
        return response;
      })
      .catch(e => {
        log.error(e, 'update-stock-failed', { listingId, oldTotal, newTotal });
        return rejectWithValue(storableError(e));
      });
  }
);
// Backward compatible wrappers for the thunks
// Set stock if requested among listing update info
export const compareAndSetStock = (listingId, oldTotal, newTotal) => (dispatch, getState, sdk) => {
  return dispatch(setStockThunk({ listingId, oldTotal, newTotal }));
};

// Helper function to make compareAndSetStock call if stock update is needed.
const updateStockOfListingMaybe = (listingId, stockTotals, dispatch) => {
  const { oldTotal, newTotal } = stockTotals || {};
  // Note: newTotal and oldTotal must be given, but oldTotal can be null
  const hasStockTotals = newTotal >= 0 && typeof oldTotal !== 'undefined';

  if (listingId && hasStockTotals) {
    return dispatch(compareAndSetStock(listingId, oldTotal, newTotal));
  }
  return Promise.resolve();
};

////////////////////////////
// Create Proper Listing //
//////////////////////////
// Create listing in publish state
// NOTE: we want to keep it possible to include stock management field to the first wizard form.
// this means that there needs to be a sequence of calls:
// create, set stock, show listing (to get updated currentStock entity)
// ⚠️ Not being used
export const requestCreateDraftListingPayloadCreator = ({ data, config }, { dispatch, rejectWithValue, extra: sdk }) => {
  const { id, stockUpdate, images, ...rest } = data;

  // If images should be saved, create array out of the image UUIDs for the API call
  // Note: in this template, image upload is not happening at the same time as listing creation.
  const imageProperty = typeof images !== 'undefined' ? { images: imageIds(images) } : {};
  const ownListingValues = { ...imageProperty, ...rest };
  const { typeOfListing } = ownListingValues.publicData || {};
  const isBusiness = typeOfListing == LISTING_TYPE_BUSINESS;
  const { currentUser } = getState().user;
  const {
    firstName,
    lastName,
    displayName,
    publicData: uPublicData,
    protectedData: uProtectedData
  } = (currentUser && currentUser.id && currentUser.attributes.profile) || {};
  const { dbaName, zipCodeRadius = [] } = uPublicData;
  const { businessListingPublished, signUpCompleted, emailUnsubscribed = false } = uProtectedData;

  const imageVariantInfo = getImageVariantInfo(config.layout.listingImage);
  const queryParams = {
    expand: true,
    include: ['author', 'images', 'currentStock'],
    'fields.image': imageVariantInfo.fieldsImage,
    ...imageVariantInfo.imageVariants,
  };

  return (id
    ? sdk.ownListings.update({
      id: new UUID(id),
      ...ownListingValues
    }, queryParams)
    : sdk.ownListings.create(ownListingValues, queryParams))
    .then(response => {
      // Modify store to understand that we have created listing and can redirect away
      dispatch(addMarketplaceEntities(response));
      return response;
    })
    .then((response) => {
      const listingId = response.data.data.id || {};
      const businessListingId = response.data.data.id.uuid;

      const sendMailEvents = {
        dbaName,
        firstName,
        userName: firstName,
        email: currentUser.attributes.email,
        type: (isBusiness
          ? BUSINESS_SCHEDULLED_MAIL
          : EVENT_SCHEDULLED_MAIL),
        template_id: (isBusiness
          ? templateIds['BUSINESS_DRAFT_TEMPLATE_ID']
          : templateIds['EVENT_DRAFT_TEMPLATE_ID']),
        faqLink: (isBusiness
          ? REACT_APP_MARKETPLACE_ROOT_URL + "/p/how-to-use-wle#my-business"
          : REACT_APP_MARKETPLACE_ROOT_URL + "/p/how-to-use-wle#rfp"),
        deepLink: `${REACT_APP_MARKETPLACE_ROOT_URL}/l/draft/${listingId.uuid}/draft/${isBusiness ? "details" : "event-details"}/${isBusiness ? "business" : "event"}`,
        send_at: moment().add(mailTime['fourHours'], "minutes").unix(),
      };

      try {
        if (!id && !emailUnsubscribed) {
          onSetScheduledMail(sendMailEvents)
            .then(sendMailResponse => {
              const batch_ids = {};
              if (sendMailResponse && sendMailResponse["batch_id"]) {
                Object.assign(batch_ids, { [sendMailResponse["batch_id"]]: moment().unix() });
              }
              if (mailTime['fortyEightHours'] && templateIds['ENAGAGE_INCOMPLETE_SIGNUP_TEMPLATE_ID']) {
                Object.assign(sendMailEvents, {
                  send_at: moment().add(mailTime['fortyEightHours'], "minutes").unix(),
                  template_id: isBusiness
                    ? templateIds['ENAGAGE_INCOMPLETE_BUSSINESS_TEMPLATE_ID']
                    : templateIds['ENAGAGE_INCOMPLETE_EVENT_TEMPLATE_ID'],
                });
                onSetScheduledMail(sendMailEvents)
                  .then(send48HoursMailResponse => {
                    if (send48HoursMailResponse && send48HoursMailResponse["batch_id"]) {
                      Object.assign(batch_ids, { [send48HoursMailResponse["batch_id"]]: moment().unix() });
                    }
                    sdk.ownListings.update({ id: listingId, privateData: { batch_ids } })
                      .then(() => false)
                      .catch(e => e);
                  })
                  .catch(e => console.error(e, '&&& fortyEightHours &&& => e'));
              }
            });
        }

        const { attributes } = response.data.data || {};
        const {
          title,
          state,
          geolocation,
          publicData = {},
        } = attributes;
        const {
          city,
          region,
          zipcode,
          country,
          boothType,
          boothTypeChilds,
          backDropPackages,
        } = publicData || {};

        const boothTypeChild = boothType && Array.isArray(boothType) && boothTypeChilds
          ? boothType.map(st => Array.isArray(boothTypeChilds[st]) ? boothTypeChilds[st] : []).flat(1).filter(d => d)
          : [];
        const equipments = boothType && Array.isArray(boothType) && boothTypeChilds
          ? boothType.map(st => Array.isArray(boothTypeChilds[st + 'Equipment']) ? boothTypeChilds[st + 'Equipment'] : []).flat(1).filter(d => d)
          : [];

        if (isBusiness) {
          crudBusinesses({
            action: 'update',
            id: listingId.uuid,
            business: {
              id: listingId.uuid,
              boothTypes: boothType,
              boothTypeChilds: boothTypeChild,
              backDropPackages,
              equipments,
              geo_loc: {
                lat: geolocation.lat,
                lng: geolocation.lng
              },
              location: { "type": "Point", "coordinates": [geolocation.lng, geolocation.lat] },
              city,
              region,
              zipcode,
              country,
              state,
              userId: currentUser.id.uuid,
              user: {
                ...uPublicData,
                firstName,
                lastName,
                displayName,
                signUpCompleted,
                businessListingId,
                businessListingPublished,
                id: currentUser.id.uuid,
                email: currentUser.attributes.email,
              },
            },
          });
          // All all new service location which is added by the business owner              
          createAlgoliaData({
            indexName: REACT_APP_ALGOLIA_COMPANY_LISTING_INDEX,
            objectID: listingId.uuid, title, state,
            _geoloc: geolocation && geolocation.lat ? { lat: geolocation.lat, lng: geolocation.lng } : {},
            ...publicData,
            user: {
              ...uPublicData,
              firstName,
              lastName,
              displayName,
              businessListingId,
              id: currentUser.id.uuid,
              email: currentUser.attributes.email,
            }
          });
        }
      } catch (e) {
        console.error(e, '&&& onScheduledMail &&& => e');
      }

      return response;
    })
    .catch(e => {
      log.error(e, 'create-listing-draft-failed', { listingData: data });
      return rejectWithValue(storableError(e));
    });
};

export const requestCreateListingThunk = createAsyncThunk(
  'EditListingPage/requestCreateListing',
  requestCreateDraftListingPayloadCreator
);
// Backward compatible wrappers for the thunks
export const requestCreateListing = payload => (dispatch, getState, sdk) => {
  return dispatch(requestCreateListingThunk(payload)).unwrap();
};

//////////////////////////
// Create Listing Draft //
//////////////////////////
// Create listing in draft state
// NOTE: we want to keep it possible to include stock management field to the first wizard form.
// this means that there needs to be a sequence of calls:
// create, set stock, show listing (to get updated currentStock entity)
// ⚠️ Not being used
export const createListingDraftThunk = createAsyncThunk(
  'EditListingPage/createListingDraft',
  ({ data, config }, { dispatch, rejectWithValue, extra: sdk }) => {
    const { stockUpdate, images, ...rest } = data;



    // If images should be saved, create array out of the image UUIDs for the API call
    const imageProperty = typeof images !== 'undefined' ? { images: imageIds(images) } : {};
    const ownListingValues = { ...imageProperty, ...rest };
    const { typeOfListing } = ownListingValues.publicData || {};
    const isBusiness = typeOfListing == LISTING_TYPE_BUSINESS;
    const { currentUser } = getState().user;
    const {
      firstName,
      lastName,
      displayName,
      publicData: uPublicData,
      protectedData: uProtectedData
    } = (currentUser && currentUser.id && currentUser.attributes.profile) || {};
    const { dbaName, zipCodeRadius = [] } = uPublicData;
    const { businessListingPublished, signUpCompleted, emailUnsubscribed = false } = uProtectedData;

    const imageVariantInfo = getImageVariantInfo(config.layout.listingImage);
    const queryParams = {
      expand: true,
      include: ['author', 'images', 'currentStock'],
      'fields.image': imageVariantInfo.fieldsImage,
      ...imageVariantInfo.imageVariants,
    };

    return (id
      ? sdk.ownListings.update({
        id: new UUID(id),
        ...ownListingValues
      }, queryParams)
      :
      sdk.ownListings
        .createDraft(ownListingValues, queryParams)
        .then(response => {
          // Modify store to understand that we have created listing and can redirect away
          dispatch(addMarketplaceEntities(response));
          // If stockUpdate info is passed through, update stock
          return updateStockOfListingMaybe(listingId, stockUpdate, dispatch).then((response) => {
            const listingId = response.data.data.id || {};
            const businessListingId = response.data.data.id.uuid;
            const sendMailEvents = {
              dbaName,
              firstName,
              userName: firstName,
              email: currentUser.attributes.email,
              type: (isBusiness
                ? BUSINESS_SCHEDULLED_MAIL
                : EVENT_SCHEDULLED_MAIL),
              template_id: (isBusiness
                ? templateIds['BUSINESS_DRAFT_TEMPLATE_ID']
                : templateIds['EVENT_DRAFT_TEMPLATE_ID']),
              faqLink: (isBusiness
                ? REACT_APP_MARKETPLACE_ROOT_URL + "/p/how-to-use-wle#my-business"
                : REACT_APP_MARKETPLACE_ROOT_URL + "/p/how-to-use-wle#rfp"),
              deepLink: `${REACT_APP_MARKETPLACE_ROOT_URL}/l/draft/${listingId.uuid}/draft/${isBusiness ? "details" : "event-details"}/${isBusiness ? "business" : "event"}`,
              send_at: moment().add(mailTime['fourHours'], "minutes").unix(),
            };

            try {
              if (!id && !emailUnsubscribed) {
                onSetScheduledMail(sendMailEvents)
                  .then(sendMailResponse => {
                    const batch_ids = {};
                    if (sendMailResponse && sendMailResponse["batch_id"]) {
                      Object.assign(batch_ids, { [sendMailResponse["batch_id"]]: moment().unix() });
                    }
                    if (mailTime['fortyEightHours'] && templateIds['ENAGAGE_INCOMPLETE_SIGNUP_TEMPLATE_ID']) {
                      Object.assign(sendMailEvents, {
                        send_at: moment().add(mailTime['fortyEightHours'], "minutes").unix(),
                        template_id: isBusiness
                          ? templateIds['ENAGAGE_INCOMPLETE_BUSSINESS_TEMPLATE_ID']
                          : templateIds['ENAGAGE_INCOMPLETE_EVENT_TEMPLATE_ID'],
                      });
                      onSetScheduledMail(sendMailEvents)
                        .then(send48HoursMailResponse => {
                          if (send48HoursMailResponse && send48HoursMailResponse["batch_id"]) {
                            Object.assign(batch_ids, { [send48HoursMailResponse["batch_id"]]: moment().unix() });
                          }
                          sdk.ownListings.update({ id: listingId, privateData: { batch_ids } })
                            .then(() => false)
                            .catch(e => e);
                        })
                        .catch(e => console.error(e, '&&& fortyEightHours &&& => e'));
                    }
                  });
              }

              const { attributes } = response.data.data || {};
              const {
                title,
                state,
                geolocation,
                publicData = {},
              } = attributes;
              const {
                city,
                region,
                zipcode,
                country,
                boothType,
                boothTypeChilds,
                backDropPackages,
              } = publicData || {};

              const boothTypeChild = boothType && Array.isArray(boothType) && boothTypeChilds
                ? boothType.map(st => Array.isArray(boothTypeChilds[st]) ? boothTypeChilds[st] : []).flat(1).filter(d => d)
                : [];
              const equipments = boothType && Array.isArray(boothType) && boothTypeChilds
                ? boothType.map(st => Array.isArray(boothTypeChilds[st + 'Equipment']) ? boothTypeChilds[st + 'Equipment'] : []).flat(1).filter(d => d)
                : [];

              if (isBusiness) {
                crudBusinesses({
                  action: 'update',
                  id: listingId.uuid,
                  business: {
                    id: listingId.uuid,
                    boothTypes: boothType,
                    boothTypeChilds: boothTypeChild,
                    backDropPackages,
                    equipments,
                    geo_loc: {
                      lat: geolocation.lat,
                      lng: geolocation.lng
                    },
                    location: { "type": "Point", "coordinates": [geolocation.lng, geolocation.lat] },
                    city,
                    region,
                    zipcode,
                    country,
                    state,
                    userId: currentUser.id.uuid,
                    user: {
                      ...uPublicData,
                      firstName,
                      lastName,
                      displayName,
                      signUpCompleted,
                      businessListingId,
                      businessListingPublished,
                      id: currentUser.id.uuid,
                      email: currentUser.attributes.email,
                    },
                  },
                });
                // All all new service location which is added by the business owner              
                createAlgoliaData({
                  indexName: REACT_APP_ALGOLIA_COMPANY_LISTING_INDEX,
                  objectID: listingId.uuid, title, state,
                  _geoloc: geolocation && geolocation.lat ? { lat: geolocation.lat, lng: geolocation.lng } : {},
                  ...publicData,
                  user: {
                    ...uPublicData,
                    firstName,
                    lastName,
                    displayName,
                    businessListingId,
                    id: currentUser.id.uuid,
                    email: currentUser.attributes.email,
                  }
                });
              }
            } catch (e) {
              console.error(e, '&&& onScheduledMail &&& => e');
            }

            return response;
          })
            .catch(e => {
              log.error(e, 'create-listing-draft-failed', { listingData: data });
              return rejectWithValue(storableError(e));
            });
        }))
  }
);

// Backward compatible wrappers for the thunks
// onCreateListingDraft
export const requestCreateListingDraft = (data, config) => (dispatch, getState, sdk) => {
  const { stockUpdate, images, ...rest } = data;

  // If images should be saved, create array out of the image UUIDs for the API call
  // Note: in this template, image upload is not happening at the same time as listing creation.
  const imageProperty = typeof images !== 'undefined' ? { images: imageIds(images) } : {};
  const ownListingValues = { ...imageProperty, ...rest };
  const { typeOfListing } = ownListingValues.publicData || {};
  const isBusiness = typeOfListing == LISTING_TYPE_BUSINESS;
  const isEventListing = typeOfListing == LISTING_TYPE_EVENT

  const { currentUser } = getState().user;
  const { firstName, publicData: uPublicData, protectedData: uProtectedData } = (currentUser && currentUser.id && currentUser.attributes.profile) || {};
  const { dbaName = 'NA', zipCodeRadius = [] } = uPublicData;
  const { emailUnsubscribed = false } = uProtectedData;

  const imageVariantInfo = getImageVariantInfo(config.layout.listingImage);
  const queryParams = {
    expand: true,
    include: ['author', 'images', 'currentStock'],
    'fields.image': imageVariantInfo.fieldsImage,
    ...imageVariantInfo.imageVariants,
  };

  // return dispatch(createListingDraftThunk({ data, config })).unwrap();


  return sdk.ownListings.createDraft(ownListingValues, queryParams)
    .then(response => {
      const businessListingId = response.data.data.id.uuid;

      if (isBusiness) {
        Object.assign(zipCodeRadius[0], {
          businessId: businessListingId,
          isDeleted: false,
        });
        sdk.currentUser.updateProfile({
          publicData: { zipCodeRadius },
          protectedData: {
            businessListingPublished: response.data.data.attributes.state,
            businessListingId,
          }
        }, { expand: true })
          .then(response => {
            const entities = denormalisedResponseEntities(response);
            if (entities.length !== 1) {
              throw new Error('Expected a resource in the sdk.currentUser.updateProfile response');
            }
            const currentUser = entities[0];

            // Update current user in state.user.currentUser through user.duck.js
            dispatch(currentUserShowSuccess(currentUser));
          });
      }
      // Modify store to understand that we have created listing and can redirect away
      dispatch(addMarketplaceEntities(response));
      return response;
    })
    .then((response) => {
      const listingId = response.data.data.id || {};
      const { attributes } = response.data.data || {};
      const { title, state: listingState, geolocation, publicData = {} } = attributes;
      const { city, region, zipcode, country } = publicData || {};
      const payload = prepareAlgoliaPayload({
        currentUser,
        listing: response.data.data,
      })

      // Add Algolia indexing for business event listing
      if (isEventListing) {
        createAlgoliaData({
          indexName: REACT_APP_ALGOLIA_EVENT_LISTING_INDEX,
          ...payload
        }).catch(err => console.error('failed to create listing in algolia', err));
      }

      const sendMailEvents = {
        dbaName,
        firstName,
        userName: firstName,
        email: currentUser.attributes.email,
        type: (isBusiness
          ? BUSINESS_SCHEDULLED_MAIL
          : EVENT_SCHEDULLED_MAIL),
        template_id: (isBusiness
          ? templateIds['BUSINESS_DRAFT_TEMPLATE_ID']
          : templateIds['EVENT_DRAFT_TEMPLATE_ID']),
        faqLink: (isBusiness
          ? REACT_APP_MARKETPLACE_ROOT_URL + "/p/how-to-use-wle#my-business"
          : REACT_APP_MARKETPLACE_ROOT_URL + "/p/how-to-use-wle#rfp"),
        deepLink: `${REACT_APP_MARKETPLACE_ROOT_URL}/l/draft/${listingId.uuid}/draft/${isBusiness ? "details" : "event-details"}/${isBusiness ? "business" : "event"}`,
        send_at: moment().add(mailTime['fourHours'], "minutes").unix(),
      };

      try {
        if (!emailUnsubscribed) {
          onSetScheduledMail(sendMailEvents)
            .then(sendMailResponse => {
              const batch_ids = {};
              if (sendMailResponse && sendMailResponse["batch_id"]) {
                Object.assign(batch_ids, { [sendMailResponse["batch_id"]]: moment().unix() });
              }
              if (mailTime['fortyEightHours'] && templateIds['ENAGAGE_INCOMPLETE_SIGNUP_TEMPLATE_ID']) {
                Object.assign(sendMailEvents, {
                  send_at: moment().add(mailTime['fortyEightHours'], "minutes").unix(),
                  template_id: isBusiness
                    ? templateIds['ENAGAGE_INCOMPLETE_BUSSINESS_TEMPLATE_ID']
                    : templateIds['ENAGAGE_INCOMPLETE_EVENT_TEMPLATE_ID'],
                });
                onSetScheduledMail(sendMailEvents)
                  .then(send48HoursMailResponse => {
                    if (send48HoursMailResponse && send48HoursMailResponse["batch_id"]) {
                      Object.assign(batch_ids, { [send48HoursMailResponse["batch_id"]]: moment().unix() });
                    }
                    sdk.ownListings.update({ id: listingId, privateData: { batch_ids } });
                  })
                  .catch(e => console.error(e, '&&& fortyEightHours &&& => e'));
              }
            });
        }
      } catch (e) {
        console.error(e, '&&& onScheduledMail &&& => e');
      }

      return response;
    })
    .catch(e => {
      log.error(e, 'create-listing-draft-failed', { listingData: data });
      return dispatch(createListingDraftError(storableError(e)));
    });
};

////////////////////
// Update Listing //
////////////////////
// Update the given tab of the wizard with the given data. This saves
// the data to the listing, and marks the tab updated so the UI can
// display the state.
// NOTE: what comes to stock management, this follows the same pattern used in create listing call
// onUpdateListing
export const updateListingThunk = createAsyncThunk(
  'EditListingPage/updateListing',
  ({ tab, data, config }, { dispatch, getState, rejectWithValue, extra: sdk }) => {
    const { id, stockUpdate, images, ...rest } = data;

    // If images should be saved, create array out of the image UUIDs for the API call
    const imageProperty = typeof images !== 'undefined' ? { images: imageIds(images) } : {};
    const ownListingUpdateValues = { id, ...imageProperty, ...rest };
    const imageVariantInfo = getImageVariantInfo(config.layout.listingImage);
    const queryParams = {
      expand: true,
      include: ['author', 'images', 'currentStock'],
      'fields.image': imageVariantInfo.fieldsImage,
      ...imageVariantInfo.imageVariants,
    };

    return updateStockOfListingMaybe(id, stockUpdate, dispatch)
      .then(() => sdk.ownListings.update(ownListingUpdateValues, queryParams))
      .then(response => {
        const state = getState();
        const existingTimeZone =
          state.marketplaceData.entities.ownListing[id.uuid]?.attributes?.availabilityPlan
            ?.timezone;
        const includedTimeZone = data?.availabilityPlan?.timezone;

        // If time zone has changed, we need to fetch exceptions again
        // since week and month boundaries might have changed.
        if (!!includedTimeZone && includedTimeZone !== existingTimeZone) {
          const searchString = '';
          const firstDayOfWeek = config.localization.firstDayOfWeek;
          const listing = response.data.data;
          fetchLoadDataExceptions(dispatch, listing, searchString, firstDayOfWeek);
        }

        dispatch(addMarketplaceEntities(response));

        // Add Algolia update for event listings
        const { attributes } = response.data.data || {};
        const { title, state: listingState, geolocation, publicData = {} } = attributes;
        const { typeOfListing } = publicData || {};
        const isBusinessListing = typeOfListing == LISTING_TYPE_BUSINESS;
        const isEventListing = typeOfListing == LISTING_TYPE_EVENT

        if (isEventListing) {
          const { currentUser } = getState().user;
          const { firstName, publicData: uPublicData } = (currentUser && currentUser.id && currentUser.attributes.profile) || {};

          const payload = prepareAlgoliaPayload({
            currentUser,
            listing: response.data.data,
          });

          updateAlgoliaData({
            indexName: REACT_APP_ALGOLIA_EVENT_LISTING_INDEX,
            ...payload,
          }).catch(err => console.error('failed to update listing in algolia', err));
        }

        return { response, tab };
      })
      .catch(e => {
        log.error(e, 'update-listing-failed', { listingData: data });
        return rejectWithValue(storableError(e));
      });
  }
);


// Backward compatible wrappers for the thunks
// onUpdateListing
export const requestUpdateListing = (tab, data, config) => (dispatch, getState, sdk) => {
  return dispatch(updateListingThunk({ tab, data, config }))
    .unwrap()
    .then(({ response, tab }) => {
      return response;
    });
};

/////////////////////
// Publish Listing //
/////////////////////

const publishListingPayloadCreator = ({ listingId }, { dispatch, getState, rejectWithValue, extra: sdk }) => {
  return sdk.ownListings
    .publishDraft({ id: listingId }, { expand: true })
    .then(async response => {
      // Add the created listing to the marketplace data
      dispatch(addMarketplaceEntities(response));

      // Update Algolia record when listing is published
      const listing = response.data.data;
      const { attributes } = listing;
      const { publicData = {}, state, geolocation } = attributes;
      const { typeOfListing } = publicData;

      const isEventListing = typeOfListing === LISTING_TYPE_EVENT;

      if (isEventListing) {
        // Prepare data for Algolia
        const { currentUser } = getState().user || {};

        try {
          const algoliaData = prepareAlgoliaPayload({
            currentUser,
            listing,
          });

          await updateAlgoliaData({
            indexName: REACT_APP_ALGOLIA_EVENT_LISTING_INDEX,
            ...algoliaData,
          });
          console.log('Algolia updated successfully on publish');
        } catch (error) {
          console.error('Failed to update Algolia on publish:', error);
        }

        const {
          city,
          region,
          country,
          postcode,
          zipcode,
          boothTypes: currentServiceKeys,
          boothType,
          boothTypeChilds,
        } = publicData;

        // Two booth-type models are in play. RFPs created since the category
        // migration store current slugs in publicData.boothTypes, with the
        // chosen subcategories under publicData[slug].subCategories. Older ones
        // store retired keys in publicData.boothType with a boothTypeChilds
        // object. Prefer the current model and fall back, rather than reading
        // only the retired key: doing that wrote a null boothTypes onto every
        // mirrored RFP, leaving the row with no service data at all.
        const usesCurrentModel = Array.isArray(currentServiceKeys) && currentServiceKeys.length > 0;

        const serviceKeys = usesCurrentModel
          ? currentServiceKeys
          : (Array.isArray(boothType) ? boothType : []);

        const boothTypeChild = usesCurrentModel
          ? currentServiceKeys
              .flatMap(slug => publicData?.[slug]?.subCategories)
              .filter(d => d)
          : (boothType && Array.isArray(boothType) && boothTypeChilds
            ? boothType.map(st => Array.isArray(boothTypeChilds[st]) ? boothTypeChilds[st] : []).flat(1).filter(d => d)
            : []);

        // Equipment and backdrop preference exist only in the retired model.
        // The current per-service objects carry no equivalent, so these stay
        // empty for current RFPs rather than being invented.
        const equipments = !usesCurrentModel && boothType && Array.isArray(boothType) && boothTypeChilds
          ? boothType.map(st => Array.isArray(boothTypeChilds[st + 'Equipment']) ? boothTypeChilds[st + 'Equipment'] : []).flat(1).filter(d => d)
          : [];
        const backDropPackages = !usesCurrentModel && boothType && Array.isArray(boothType) && boothTypeChilds
          ? boothType.map(st => boothTypeChilds[st + 'backdropPreference']).filter(d => d)
          : [];

        // Mirror the published RFP into MongoDB. The match algorithm and the admin
        // dashboard read this collection, so a missing row makes the RFP invisible
        // to both. Skipped when the listing has no geolocation, since the stored
        // GeoJSON point is required.
        if (geolocation && geolocation.lat) {
          try {
            await crudEvents({
              action: 'update',
              id: listing.id.uuid,
              event: {
                id: listing.id.uuid,
                userId: currentUser && currentUser.id ? currentUser.id.uuid : undefined,
                boothTypes: serviceKeys,
                boothTypeChilds: boothTypeChild,
                backDropPackages,
                equipments,
                geo_loc: {
                  lat: geolocation.lat,
                  lng: geolocation.lng
                },
                location: { "type": "Point", "coordinates": [geolocation.lng, geolocation.lat] },
                city,
                region,
                zipcode: postcode || zipcode || null,
                country,
                state,
              },
            });
            console.log('RFP mirrored to MongoDB successfully on publish');
          } catch (error) {
            console.error('Failed to mirror RFP to MongoDB on publish:', error);
          }
        } else {
          console.error('Skipped RFP MongoDB mirror on publish: listing has no geolocation');
        }

        // Notify providers whose service area and booth types match this RFP.
        if (currentUser && currentUser.id) {
          try {
            const categories = await fetchNewCategories({})
              .then(res => (res && res.data) || [])
              .catch(() => []);

            await onSendEventMatchMail(sendEventMailData(listing, currentUser, categories));
            console.log('RFP match emails queued successfully on publish');
          } catch (error) {
            console.error('Failed to send RFP match emails on publish:', error);
          }
        }
      }

      return response;
    })
    .catch(e => {
      return rejectWithValue(storableError(e));
    });
};

export const publishListingThunk = createAsyncThunk(
  'EditListingPage/publishListing',
  publishListingPayloadCreator
);
// Backward compatible wrappers for the thunks
export const requestPublishListingDraft = listingId => (dispatch, getState, sdk) => {
  return dispatch(publishListingThunk({ listingId })).unwrap();
};

//////////////////
// Upload Image //
//////////////////

// Images return imageId which we need to map with previously generated temporary id
export const uploadImageThunk = createAsyncThunk(
  'EditListingPage/uploadImage',
  ({ actionPayload, listingImageConfig }, { rejectWithValue, extra: sdk }) => {
    const imageVariantInfo = getImageVariantInfo(listingImageConfig);
    const queryParams = {
      expand: true,
      'fields.image': imageVariantInfo.fieldsImage,
      ...imageVariantInfo.imageVariants,
    };

    return sdk.images
      .upload({ image: actionPayload.file }, queryParams)
      .then(resp => {
        const img = resp.data.data;
        // Uploaded image has an existing id that refers to file
        // The UUID was created as a consequence of this upload call - it's saved to imageId property
        return {
          data: { ...img, id: actionPayload.id, imageId: img.id, file: actionPayload.file },
        };
      })
      .catch(e => {
        return rejectWithValue({ id: actionPayload.id, error: storableError(e) });
      });
  }
);
// Backward compatible wrappers for the thunks
export const requestImageUpload = (actionPayload, listingImageConfig) => (
  dispatch,
  getState,
  sdk
) => {
  return dispatch(uploadImageThunk({ actionPayload, listingImageConfig })).unwrap();
};

///////////////////////////////
// Add AvailabilityException //
///////////////////////////////
const addAvailabilityExceptionPayloadCreator = ({ params }, { rejectWithValue, extra: sdk }) => {
  return sdk.availabilityExceptions
    .create(params, { expand: true })
    .then(response => {
      const availabilityException = response.data.data;
      return { data: availabilityException };
    })
    .catch(e => {
      return rejectWithValue({ error: storableError(e) });
    });
};

export const addAvailabilityExceptionThunk = createAsyncThunk(
  'EditListingPage/addAvailabilityException',
  addAvailabilityExceptionPayloadCreator
);
// Backward compatible wrappers for the thunks
export const requestAddAvailabilityException = params => (dispatch, getState, sdk) => {
  return dispatch(addAvailabilityExceptionThunk({ params })).unwrap();
};

//////////////////////////////////
// Delete AvailabilityException //
//////////////////////////////////
const deleteAvailabilityExceptionPayloadCreator = ({ params }, { rejectWithValue, extra: sdk }) => {
  return sdk.availabilityExceptions
    .delete(params, { expand: true })
    .then(response => {
      const availabilityException = response.data.data;
      return { data: availabilityException };
    })
    .catch(e => {
      return rejectWithValue({ error: storableError(e) });
    });
};

export const deleteAvailabilityExceptionThunk = createAsyncThunk(
  'EditListingPage/deleteAvailabilityException',
  deleteAvailabilityExceptionPayloadCreator
);
// Backward compatible wrappers for the thunks
export const requestDeleteAvailabilityException = params => (dispatch, getState, sdk) => {
  return dispatch(deleteAvailabilityExceptionThunk({ params })).unwrap();
};

//////////////////////////////////
// Fetch AvailabilityExceptions //
//////////////////////////////////
const fetchAvailabilityExceptionsPayloadCreator = (
  { params },
  { dispatch, rejectWithValue, extra: sdk }
) => {
  const { listingId, start, end, timeZone, page, isWeekly } = params;
  const fetchParams = { listingId, start, end };
  const timeUnitIdProp = isWeekly
    ? { weekStartId: stringifyDateToISO8601(start) }
    : { monthId: monthIdString(start, timeZone) };

  return sdk.availabilityExceptions
    .query(fetchParams)
    .then(response => {
      const availabilityExceptions = denormalisedResponseEntities(response);

      // Fetch potential extra exceptions pagination pages per month.
      const totalPages = response.data.meta.totalPages;
      if (totalPages > 1 && !page) {
        const extraPages = getArrayOfNItems(totalPages);

        Promise.all(
          extraPages.map(page => {
            return sdk.availabilityExceptions.query({ ...fetchParams, page });
          })
        ).then(responses => {
          const denormalizedFlatResults = (all, r) => all.concat(denormalisedResponseEntities(r));
          const exceptions = responses.reduce(denormalizedFlatResults, []);
          dispatch(
            fetchExtraAvailabilityExceptionsThunk.fulfilled({
              data: { ...timeUnitIdProp, exceptions },
            })
          );
        });
      }

      return {
        ...timeUnitIdProp,
        exceptions: availabilityExceptions,
      };
    })
    .catch(e => {
      return rejectWithValue({ ...timeUnitIdProp, error: storableError(e) });
    });
};

export const fetchAvailabilityExceptionsThunk = createAsyncThunk(
  'EditListingPage/fetchAvailabilityExceptions',
  fetchAvailabilityExceptionsPayloadCreator
);
// Backward compatible wrappers for the thunks
export const requestFetchAvailabilityExceptions = params => (dispatch, getState, sdk) => {
  return dispatch(fetchAvailabilityExceptionsThunk({ params })).unwrap();
};

export const fetchExtraAvailabilityExceptionsThunk = createAsyncThunk(
  'EditListingPage/fetchExtraAvailabilityExceptions',
  ({ data }) => data
);

/////////////////////////
// Save Payout Details //
/////////////////////////
const savePayoutDetailsPayloadCreator = (
  { values, isUpdateCall },
  { dispatch, rejectWithValue }
) => {
  const upsertThunk = isUpdateCall ? updateStripeAccount : createStripeAccount;

  return dispatch(upsertThunk(values, { expand: true }))
    .then(response => {
      return response;
    })
    .catch(() => {
      return rejectWithValue();
    });
};

export const savePayoutDetailsThunk = createAsyncThunk(
  'EditListingPage/savePayoutDetails',
  savePayoutDetailsPayloadCreator
);
// Backward compatible wrappers for the thunks
export const savePayoutDetails = (values, isUpdateCall) => dispatch => {
  return dispatch(savePayoutDetailsThunk({ values, isUpdateCall })).unwrap();
};

////////////////////////////////
// Fetch Load Data Exceptions //
////////////////////////////////

// Helper function for loadData call.
const fetchLoadDataExceptions = (dispatch, listing, search, firstDayOfWeek) => {
  const hasWindow = typeof window !== 'undefined';
  // Listing could be ownListing entity too, so we just check if attributes key exists
  const hasTimeZone = listing?.attributes?.availabilityPlan?.timezone;

  // Fetch time-zones on client side only.
  // Note: listing needs to have time zone set!
  if (hasWindow && listing.id && hasTimeZone) {
    const listingId = listing.id;
    // If the listing doesn't have availabilityPlan yet
    // use the defaul timezone
    const timezone = listing.attributes.availabilityPlan?.timezone || getDefaultTimeZoneOnBrowser();
    const todayInListingsTZ = getStartOf(new Date(), 'day', timezone);

    const locationSearch = parse(search);
    const selectedDate = locationSearch?.d
      ? parseDateFromISO8601(locationSearch.d, timezone)
      : todayInListingsTZ;
    const startOfWeek = getStartOfWeek(selectedDate, timezone, firstDayOfWeek);
    const prevWeek = getStartOf(startOfWeek, 'day', timezone, -7, 'days');
    const nextWeek = getStartOf(startOfWeek, 'day', timezone, 7, 'days');
    const nextAfterNextWeek = getStartOf(nextWeek, 'day', timezone, 7, 'days');

    const nextMonth = getStartOf(todayInListingsTZ, 'month', timezone, 1, 'months');
    const nextAfterNextMonth = getStartOf(nextMonth, 'month', timezone, 1, 'months');

    const sharedData = { listingId, timeZone: timezone };

    // Fetch data for selected week and nearest weeks for WeeklyCalendar
    // Plus current month and month after that for EditListingAvailabilityForm
    //
    // NOTE: This is making 5 different Thunk calls, which update store 2 times each
    //       It would make sense to make on thunk function that fires 5 sdk calls/promises,
    //       but for the time being, it's clearer to push all the calls through
    //       requestFetchAvailabilityExceptions
    return Promise.all([
      dispatch(
        requestFetchAvailabilityExceptions({
          ...sharedData,
          isWeekly: true,
          start: prevWeek,
          end: startOfWeek,
        })
      ),
      dispatch(
        requestFetchAvailabilityExceptions({
          ...sharedData,
          isWeekly: true,
          start: startOfWeek,
          end: nextWeek,
        })
      ),
      dispatch(
        requestFetchAvailabilityExceptions({
          ...sharedData,
          isWeekly: true,
          start: nextWeek,
          end: nextAfterNextWeek,
        })
      ),
      dispatch(
        requestFetchAvailabilityExceptions({
          ...sharedData,
          start: todayInListingsTZ,
          end: nextMonth,
        })
      ),
      dispatch(
        requestFetchAvailabilityExceptions({
          ...sharedData,
          start: nextMonth,
          end: nextAfterNextMonth,
        })
      ),
    ]);
  }

  // By default return an empty array
  return Promise.all([]);
};

/////////////////////////////
// Upload Images Cloudinary //
/////////////////////////////
export const uploadImagesToCloudinary = params => (dispatch, getState, sdk) => {
  const { cloudinaryImagesArray = [] } = params;

  const hasCloudinaryImages = Array.isArray(cloudinaryImagesArray) && cloudinaryImagesArray.length > 0;

  if (!hasCloudinaryImages) {
    return Promise.resolve([]);
  }

  const uploadPromises = cloudinaryImagesArray.map(file => {
    if (!file || !file.name) return Promise.resolve(null);
    // -----------------------------
    // ✅ Detect file type
    // -----------------------------
    let resourceType = "image"; // default
    const fileType = file.type.toLowerCase();

    if (fileType.startsWith("image")) {
      resourceType = "image";
    } else if (fileType.startsWith("video")) {
      resourceType = "video";
    } else if (fileType.startsWith("application")) {
      resourceType = "image";
    }

    const folderName = 'listingMedia';
    const formData = new FormData();
    formData.append('file', file);
    formData.append('folder', folderName);
    formData.append('resource_type', resourceType);
    formData.append('upload_preset', process.env.REACT_APP_CLOUDINARY_PRESET);

    const config = {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 60000, // 60 seconds timeout for larger files
    };

    const cloudName = process.env.REACT_APP_CLOUDINARY_NAME;
    if (!cloudName) {
      const errorMsg = 'Cloudinary cloud name is not configured.';
      console.error(errorMsg);
      return Promise.resolve({ error: true, message: errorMsg });
    }

    // The URL should be your Cloudinary upload URL, not your server's
    return axios
      .post(`https://api.cloudinary.com/v1_1/${cloudName}/${resourceType}/upload`, formData, config)
      .then(res => {
        const d = res.data;
        console.log('Uploaded File Response:', d);
        return {
          secure_url: d.secure_url,
          public_id: d.public_id,
          resource_type: d.resource_type,
          original_filename: d.original_filename,
        };
      })
      .catch(err => {
        console.error('Cloudinary upload error:', err.response ? err.response.data : err.message);
        return {
          error: true,
          fileName: file.name,
          message: (err.response && err.response.data && err.response.data.error && err.response.data.error.message) || 'File upload failed.',
        };
      });
  });

  return Promise.all(uploadPromises)
    .then(results => {
      // Always return full array of results for consistency
      return results;
    })
    .catch(err => {
      console.error('Error processing upload promises:', err);
      // This will be caught by the .catch in the component
      throw storableError(err);
    });
};

// fetch new categories code
const requestFetchNewCategoriesExceptionsPayloadCreator = (
  { },
  { dispatch, rejectWithValue, extra: sdk }
) => {
  return fetchNewCategories({}).then(response => {
    const categories = response?.data || [];
    return categories;
  }).catch(error => rejectWithValue(error));
};

export const requestFetchNewCategoriesThunk = createAsyncThunk(
  'CompanyProfilePage/FetchNewCategories',
  requestFetchNewCategoriesExceptionsPayloadCreator
);

// Backward compatible wrappers for the thunks
export const requestFetchNewCategories = params => (dispatch, getState, sdk) => {
  return dispatch(requestFetchNewCategoriesThunk({ params })).unwrap();
};

// ================ Slice ================ //

const initialState = {
  // Error instance placeholders for each endpoint
  createListingDraftError: null,
  listingId: null,
  publishListingError: null,
  updateListingError: null,
  showListingsError: null,
  uploadImageError: null,
  setStockError: null,
  setStockInProgress: false,
  createListingDraftInProgress: false,
  submittedListingId: null,
  redirectToListing: false,
  uploadedImages: {},
  uploadedImagesOrder: [],
  removedImageIds: [],
  addExceptionError: null,
  addExceptionInProgress: false,
  weeklyExceptionQueries: {
    // '2022-12-12': { // Note: id/key is the start of the week in given time zone
    //   fetchExceptionsError: null,
    //   fetchExceptionsInProgress: null,
    // },
  },
  monthlyExceptionQueries: {
    // '2022-12': {
    //   fetchExceptionsError: null,
    //   fetchExceptionsInProgress: null,
    // },
  },
  allExceptions: [],
  deleteExceptionError: null,
  deleteExceptionInProgress: false,
  listingDraft: null,
  updatedTab: null,
  updateInProgress: false,
  payoutDetailsSaveInProgress: false,
  payoutDetailsSaved: false,
  categories: [], // Add categories to initial state
  fetchCategoriesInProgress: false,
  fetchCategoriesError: null,
};

const editListingPageSlice = createSlice({
  name: 'EditListingPage',
  initialState,
  reducers: {
    markTabUpdated: (state, action) => {
      state.updatedTab = action.payload;
    },
    clearUpdatedTab: state => {
      state.updatedTab = null;
      state.updateListingError = null;
    },
    clearPublishError: state => {
      state.publishListingError = null;
    },
    removeListingImage: (state, action) => {
      const id = action.payload;

      // Only mark the image removed if it hasn't been added to the
      // listing already
      const removedImageIds = state.uploadedImages[id]
        ? state.removedImageIds
        : state.removedImageIds.concat(id);

      // Always remove from the draft since it might be a new image to
      // an existing listing.
      const uploadedImages = omit(state.uploadedImages, id);
      const uploadedImagesOrder = state.uploadedImagesOrder.filter(i => i !== id);

      state.uploadedImages = uploadedImages;
      state.uploadedImagesOrder = uploadedImagesOrder;
      state.removedImageIds = removedImageIds;
    },
  },
  extraReducers: builder => {
    builder
      // createListingDraft cases
      .addCase(createListingDraftThunk.pending, state => {
        state.createListingDraftInProgress = true;
        state.createListingDraftError = null;
        state.submittedListingId = null;
        state.listingDraft = null;
      })
      .addCase(createListingDraftThunk.fulfilled, (state, action) => {
        const updatedImagesState = updateUploadedImagesState(state, action.payload.data);
        state.uploadedImages = updatedImagesState.uploadedImages;
        state.uploadedImagesOrder = updatedImagesState.uploadedImagesOrder;
        state.createListingDraftInProgress = false;
        state.submittedListingId = action.payload.data.id;
        state.listingDraft = action.payload.data;
      })
      .addCase(createListingDraftThunk.rejected, (state, action) => {
        state.createListingDraftInProgress = false;
        state.createListingDraftError = action.payload;
      })
      // Proper Create Listing cases
      .addCase(requestCreateListingThunk.pending, (state, action) => {
        state.createListingInProgress = true;
        state.createListingError = null;
      })
      .addCase(requestCreateListingThunk.fulfilled, (state, action) => {
        state.createListingInProgress = false;
        state.createListingError = null;
        state.listingId = action.payload.data.id;
      })
      .addCase(requestCreateListingThunk.rejected, (state, action) => {
        state.createListingInProgress = false;
        state.createListingError = action.payload;
      })
      // publishListing cases
      .addCase(publishListingThunk.pending, (state, action) => {
        state.listingId = action.meta.arg.listingId;
        state.publishListingError = null;
      })
      .addCase(publishListingThunk.fulfilled, state => {
        state.redirectToListing = true;
        state.createListingDraftError = null;
        state.updateListingError = null;
        state.showListingsError = null;
        state.uploadImageError = null;
        state.createListingDraftInProgress = false;
        state.updateInProgress = false;
      })
      .addCase(publishListingThunk.rejected, (state, action) => {
        // eslint-disable-next-line no-console
        console.error(action.payload);
        state.publishListingError = {
          listingId: state.listingId,
          error: action.payload,
        };
      })
      // updateListing cases
      .addCase(updateListingThunk.pending, state => {
        state.updateInProgress = true;
        state.updateListingError = null;
      })
      .addCase(updateListingThunk.fulfilled, (state, action) => {
        const updatedImagesState = updateUploadedImagesState(state, action.payload.response.data);
        state.uploadedImages = updatedImagesState.uploadedImages;
        state.uploadedImagesOrder = updatedImagesState.uploadedImagesOrder;
        state.updateInProgress = false;
        state.updatedTab = action.payload.tab;
      })
      .addCase(updateListingThunk.rejected, (state, action) => {
        state.updateInProgress = false;
        state.updateListingError = action.payload;
      })
      // showListing cases
      .addCase(showListingThunk.pending, state => {
        state.showListingsError = null;
      })
      .addCase(showListingThunk.fulfilled, (state, action) => {
        const listingIdFromPayload = action.payload.data.id;
        const { listingId, allExceptions, weeklyExceptionQueries, monthlyExceptionQueries } = state;
        // If listing stays the same, we trust previously fetched exception data.
        if (listingIdFromPayload?.uuid === state.listingId?.uuid) {
          Object.assign(state, initialState);
          state.listingId = listingId;
          state.allExceptions = allExceptions;
          state.weeklyExceptionQueries = weeklyExceptionQueries;
          state.monthlyExceptionQueries = monthlyExceptionQueries;
        } else {
          Object.assign(state, initialState);
          state.listingId = listingIdFromPayload;
        }
      })
      .addCase(showListingThunk.rejected, (state, action) => {
        // eslint-disable-next-line no-console
        console.error(action.payload);
        state.showListingsError = action.payload;
        state.redirectToListing = false;
      })
      // uploadImage cases
      .addCase(uploadImageThunk.pending, (state, action) => {
        const params = action.meta.arg.actionPayload;
        const id = params.id;
        // payload.params: { id: 'tempId', file }
        const uploadedImages = {
          ...state.uploadedImages,
          [id]: { ...params },
        };
        state.uploadedImages = uploadedImages;
        state.uploadedImagesOrder = state.uploadedImagesOrder.concat([id]);
        state.uploadImageError = null;
      })
      .addCase(uploadImageThunk.fulfilled, (state, action) => {
        // payload.data: { id: 'tempId', imageId: 'some-real-id', attributes, type }
        const { id, ...rest } = action.payload.data;
        state.uploadedImages[id] = { id, ...rest };
      })
      .addCase(uploadImageThunk.rejected, (state, action) => {
        const { id, error } = action.payload;
        state.uploadedImagesOrder = state.uploadedImagesOrder.filter(i => i !== id);
        state.uploadedImages = omit(state.uploadedImages, id);
        state.uploadImageError = error;
      })
      // setStock cases
      .addCase(setStockThunk.pending, state => {
        state.setStockInProgress = true;
        state.setStockError = null;
      })
      .addCase(setStockThunk.fulfilled, state => {
        state.setStockInProgress = false;
      })
      .addCase(setStockThunk.rejected, (state, action) => {
        state.setStockInProgress = false;
        state.setStockError = action.payload;
      })
      // fetchAvailabilityExceptions cases
      .addCase(fetchAvailabilityExceptionsThunk.pending, (state, action) => {
        const { isWeekly, start, timeZone } = action.meta.arg.params;
        const weekStartId = isWeekly ? stringifyDateToISO8601(start) : null;
        const monthId = !isWeekly ? monthIdString(start, timeZone) : null;
        const newData = { fetchExceptionsError: null, fetchExceptionsInProgress: true };

        if (monthId) {
          state.monthlyExceptionQueries[monthId] = {
            ...state.monthlyExceptionQueries[monthId],
            ...newData,
          };
        } else if (weekStartId) {
          state.weeklyExceptionQueries[weekStartId] = {
            ...state.weeklyExceptionQueries[weekStartId],
            ...newData,
          };
        }
      })
      .addCase(fetchAvailabilityExceptionsThunk.fulfilled, (state, action) => {
        const { exceptions, monthId, weekStartId } = action.payload;
        const combinedExceptions = state.allExceptions.concat(exceptions);
        const selectId = x => x.id.uuid;
        state.allExceptions = uniqueBy(combinedExceptions, selectId).sort(
          sortExceptionsByStartTime
        );

        const newData = { fetchExceptionsInProgress: false };
        if (monthId) {
          state.monthlyExceptionQueries[monthId] = {
            ...state.monthlyExceptionQueries[monthId],
            ...newData,
          };
        } else if (weekStartId) {
          state.weeklyExceptionQueries[weekStartId] = {
            ...state.weeklyExceptionQueries[weekStartId],
            ...newData,
          };
        }
      })
      .addCase(fetchAvailabilityExceptionsThunk.rejected, (state, action) => {
        const { monthId, weekStartId, error } = action.payload;
        const newData = { fetchExceptionsInProgress: false, fetchExceptionsError: error };

        if (monthId) {
          state.monthlyExceptionQueries[monthId] = {
            ...state.monthlyExceptionQueries[monthId],
            ...newData,
          };
        } else if (weekStartId) {
          state.weeklyExceptionQueries[weekStartId] = {
            ...state.weeklyExceptionQueries[weekStartId],
            ...newData,
          };
        }
      })
      // fetchExtraAvailabilityExceptions cases
      .addCase(fetchExtraAvailabilityExceptionsThunk.fulfilled, (state, action) => {
        const combinedExceptions = state.allExceptions.concat(action.payload.exceptions);
        const selectId = x => x.id.uuid;
        state.allExceptions = uniqueBy(combinedExceptions, selectId).sort(
          sortExceptionsByStartTime
        );
      })
      // addAvailabilityException cases
      .addCase(addAvailabilityExceptionThunk.pending, state => {
        state.addExceptionError = null;
        state.addExceptionInProgress = true;
      })
      .addCase(addAvailabilityExceptionThunk.fulfilled, (state, action) => {
        const exception = action.payload.data;
        const combinedExceptions = state.allExceptions.concat(exception);
        state.allExceptions = combinedExceptions.sort(sortExceptionsByStartTime);
        state.addExceptionInProgress = false;
      })
      .addCase(addAvailabilityExceptionThunk.rejected, (state, action) => {
        state.addExceptionError = action.payload.error;
        state.addExceptionInProgress = false;
      })
      // deleteAvailabilityException cases
      .addCase(deleteAvailabilityExceptionThunk.pending, state => {
        state.deleteExceptionError = null;
        state.deleteExceptionInProgress = true;
      })
      .addCase(deleteAvailabilityExceptionThunk.fulfilled, (state, action) => {
        const exception = action.payload.data;
        const id = exception.id.uuid;
        state.allExceptions = state.allExceptions.filter(e => e.id.uuid !== id);
        state.deleteExceptionInProgress = false;
      })
      .addCase(deleteAvailabilityExceptionThunk.rejected, (state, action) => {
        state.deleteExceptionError = action.payload.error;
        state.deleteExceptionInProgress = false;
      })
      // savePayoutDetails cases
      .addCase(savePayoutDetailsThunk.pending, state => {
        state.payoutDetailsSaveInProgress = true;
      })
      .addCase(savePayoutDetailsThunk.fulfilled, state => {
        state.payoutDetailsSaveInProgress = false;
        state.payoutDetailsSaved = true;
      })
      .addCase(savePayoutDetailsThunk.rejected, state => {
        state.payoutDetailsSaveInProgress = false;
      })
      // fetchCategories
      .addCase(requestFetchNewCategoriesThunk.pending, state => {
        state.fetchCategoriesInProgress = true;
        state.fetchCategoriesError = null;
      })
      .addCase(requestFetchNewCategoriesThunk.fulfilled, (state, action) => {
        state.categories = action.payload;
        state.fetchCategoriesInProgress = false;
        state.fetchCategoriesError = null;
      })
      .addCase(requestFetchNewCategoriesThunk.rejected, (state, action) => {
        state.fetchCategoriesInProgress = false;
        state.fetchCategoriesError = action.payload;
      });
  },
});

export const {
  markTabUpdated,
  clearUpdatedTab,
  clearPublishError,
  removeListingImage,
} = editListingPageSlice.actions;
export default editListingPageSlice.reducer;

// ================ Load data ================ //

// loadData is run for each tab of the wizard. When editing an
// existing listing, the listing must be fetched first.
export const loadData = (params, search, config) => (dispatch, getState, sdk) => {
  dispatch(clearUpdatedTab());
  dispatch(clearPublishError());
  const { id, type } = params;
  const fetchCurrentUserOptions = {
    updateNotifications: false,
  };

  if (type === 'new') {
    // No need to listing data when creating a new listing
    return Promise.all([dispatch(fetchCurrentUser(fetchCurrentUserOptions))])
      .then(response => {
        const currentUser = getState().user.currentUser;
        if (currentUser && currentUser.stripeAccount) {
          dispatch(fetchStripeAccount());
        }
        return response;
      })
      .catch(e => {
        throw e;
      });
  }

  const payload = { id: new UUID(id) };
  return Promise.all([
    dispatch(requestShowListing(payload, config)),
    dispatch(fetchCurrentUser(fetchCurrentUserOptions)),
  ])
  .then(response => {
    const currentUser = getState().user.currentUser;
    dispatch(requestFetchNewCategories());

      // Do not fetch extra information if user is in pending-approval state.
      if (isUserAuthorized(currentUser)) {
        if (currentUser && currentUser.stripeAccount) {
          dispatch(fetchStripeAccount());
        }

        // Because of two dispatch functions, response is an array.
        // We are only interested in the response from requestShowListing here,
        // so we need to pick the first one
        const listing = response[0]?.data?.data;
        const transactionProcessAlias = listing?.attributes?.publicData?.transactionProcessAlias;
        if (listing && isBookingProcessAlias(transactionProcessAlias)) {
          fetchLoadDataExceptions(dispatch, listing, search, config.localization.firstDayOfWeek);
        }
      }

      return response;
    })
    .catch(e => {
      throw e;
    });
};